import { getStoredFilters, HeaderOptions, updateStoredFilters } from '../common/reports-constants';
import Header from '../../../components/common/global-top-bar';
import SwitchBar from '../../../components/switch-bar';
import { useState } from 'react';
import { DebounceInput } from 'react-debounce-input';
import { EngagementReportFilters } from '../common/reports-constants';
import { EngagementSummary } from './summary';
import { EngagementDetailsReport } from './details';
import { useEngagementReportDownload, useEngagementSummaryReportDownload } from '../../../hooks/useEngagementReporting';
import { Toggle } from '../../../components/ui';
import { useAllMinimalClients } from '../../../hooks/useClients';

enum EngagementReportType {
  Summary = 'Summary',
  Details = 'Details',
}

const EngagementReporting: React.FC = () => {
  const today = new Date();
  const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

  const [filters, setFilters] = useState<EngagementReportFilters>(() => {
    const defaultFilters: EngagementReportFilters = {
      startDate: firstDayOfCurrentMonth.toISOString().split('T')[0],
      endDate: firstDayOfNextMonth.toISOString().split('T')[0],
      clientId: undefined,
      testData: false,
    };
    return getStoredFilters(defaultFilters);
  });

  const [reportType, setReportType] = useState<EngagementReportType>(EngagementReportType.Summary);

  return (
    <div>
      <Header heading={'Engagement Reporting'} />
      <SwitchBar heading={HeaderOptions} position={1} />
      <Filters filters={filters} setFilters={setFilters} reportType={reportType} setReportType={setReportType} />

      <div>
        <div className="max-window-height-connections overflow-y-auto px-7 pt-7 font-['Montserrat']">
          {reportType === EngagementReportType.Summary && <EngagementSummary filters={filters} />}
          {reportType === EngagementReportType.Details && <EngagementDetailsReport filters={filters} />}
        </div>
      </div>
    </div>
  );
};

interface FilterProps {
  filters: EngagementReportFilters;
  setFilters: (filters: EngagementReportFilters) => void;
  reportType: EngagementReportType;
  setReportType: (reportType: EngagementReportType) => void;
}

const Filters: React.FC<FilterProps> = ({ filters, setFilters, reportType, setReportType }) => {
  const { data: clients } = useAllMinimalClients();
  const { isFetching: isFetchingDetails, refetch: refetchDetails } = useEngagementReportDownload(filters, false);
  const { isFetching: isFetchingSummary, refetch: refetchSummary } = useEngagementSummaryReportDownload(filters, false);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
    updateStoredFilters(name, value);
  };

  const handleInputChange = (filter: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFilters({
      ...filters,
      [filter]: value && value.length ? parseInt(event.target.value) : undefined,
    });
  };

  const handleDownload = () => {
    if (filters.startDate !== '' && filters.endDate !== '') {
      const fetchData = async () => {
        (await reportType) === EngagementReportType.Details ? refetchDetails() : refetchSummary();
      };
      fetchData();
    }
  };

  return (
    <div className="gray-border-line bg-gray-background-light flex h-10 w-full items-center space-x-2 border-b px-4">
      <div className="gray-background-dark flex items-center py-4">
        {(Object.keys(EngagementReportType) as Array<keyof typeof EngagementReportType>).map((item) => (
          <button
            key={item}
            className={
              'whitespace-nowrap px-2 py-1 text-sm' +
              (EngagementReportType[item] === reportType
                ? ' bg-blue-primary text-white'
                : ' bg-gray-background-dark text-gray-dark')
            }
            onClick={() => {
              setReportType(EngagementReportType[item]);
            }}
          >
            {EngagementReportType[item]}
          </button>
        ))}
      </div>
      <div className="flex items-center space-x-2">
        <label className="whitespace-nowrap text-sm">Start Date:</label>
        <input
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleDateChange}
          className="w-24 rounded border p-0.5 text-xs focus:ring-transparent"
        />
        <label className="whitespace-nowrap text-sm">End Date:</label>
        <input
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleDateChange}
          className="w-24 rounded border p-0.5 text-xs focus:ring-transparent"
        />
        <label className="flex items-center whitespace-nowrap text-sm">
          Group:
          <select
            className="ml-1 h-6 w-60 rounded px-1 py-0.5 text-sm focus:ring-transparent"
            onChange={(e) => {
              const value = e.target.value;
              setFilters({
                ...filters,
                clientId: value ? parseInt(value) : undefined,
              });
            }}
          >
            <option value="">All</option>
            {clients?.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-x-2 text-sm">
        <div className="whitespace-nowrap">Include Test Users</div>
        <Toggle
          checked={!!filters.testData}
          onChange={() => {
            setFilters({ ...filters, testData: !filters.testData });
          }}
        />
      </div>
      <div className="">
        <button
          className="bg-blue-primary rounded-full px-4 py-1 text-white"
          onClick={handleDownload}
          disabled={isFetchingSummary || isFetchingDetails}
        >
          Download
        </button>
      </div>
    </div>
  );
};

export default EngagementReporting;
